/**
 * N2AboutPage — credentials-first. Who's behind Notches, what the parent
 * company is, what the regulatory posture is.
 *
 * Voice: understated, factual, no self-promotion. Bobby's background gets
 * the space it deserves without sounding like a bio for a keynote.
 */

import React from 'react';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import {
  Eyebrow, PageTitle, PageLede, SectionLabel, SectionHead, Body,
} from './N2Primitives';

export const N2AboutPage: React.FC = () => (
  <N2Frame page="about">
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '64px 32px 40px' }}>
      <Eyebrow>About</Eyebrow>
      <PageTitle italic="who's behind the readings.">
        Notches is a product of WFS —
      </PageTitle>
      <PageLede>
        Notches is the research and trading platform from Wealth Financial
        Systems. It brings together public market research, researched
        opportunities, and supported automated trading capabilities. What
        follows is who we are, how the platform is organized, and the
        regulatory boundary we operate within.
      </PageLede>

      <FounderSection />
      <CompanySection />
      <PostureSection />
    </div>
  </N2Frame>
);

// ---- Founder --------------------------------------------------------------

const FounderSection: React.FC = () => (
  <section style={{ marginTop: 64 }}>
    <SectionLabel left="Founder" right="Bobby Malik" />
    <div style={{ marginTop: 32 }}>
      <SectionHead>
        Twenty years in engineering. Eight in quantitative research. One book on
        why boring wins.
      </SectionHead>
      <Body>
        Bobby's day job for two decades was building infrastructure at scale
        for one of the largest technology companies in the world. The tools
        that run Notches — deterministic risk engines, backtest harnesses,
        privacy-preserving data pipelines — are built with the same standards.
      </Body>
      <Body>
        The quantitative work started as personal research: what does dealer
        positioning actually predict, how do you measure IV without lying to
        yourself about mean-reversion, what makes an anchored VWAP fire in
        one regime and fail in another. Notches is that research turned
        into a product.
      </Body>
      <Body>
        The book — <em>Get Rich the Boring Way</em> — makes the case for
        boring as a strategy, not a personality trait. Notches is the tooling
        that reinforces the argument.
      </Body>
    </div>
  </section>
);

// ---- Company --------------------------------------------------------------

const CompanySection: React.FC = () => (
  <section style={{ marginTop: 96 }}>
    <SectionLabel left="Company" right="WFS · Wealth Financial Systems" />
    <div style={{
      marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32,
    }} className="n2-company-grid">
      <FactCard label="Parent entity" value="Wealth Financial Systems" note="WFS is the operating company. Notches is the product." />
      <FactCard label="Founded" value="2024" note="First research letter published Q3 2026." />
      <FactCard label="Team size" value="Small on purpose" note="A tight desk. Every letter is written by one person." />
      <FactCard label="Location" value="United States" note="Operations run out of the U.S. Subscribers globally." />
    </div>
  </section>
);

const FactCard: React.FC<{ label: string; value: string; note: string }> = ({ label, value, note }) => (
  <div style={{
    padding: '24px 28px', border: `1px solid ${palette.line}`,
    background: palette.paper, borderRadius: 3,
  }}>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
    }}>{label}</div>
    <div style={{
      fontFamily: 'var(--serif)', fontSize: 22, color: palette.ink,
      marginTop: 8, lineHeight: 1.15, letterSpacing: '-0.005em',
    }}>{value}</div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      marginTop: 10, lineHeight: 1.5,
    }}>{note}</div>
  </div>
);

// ---- Posture --------------------------------------------------------------

const PostureSection: React.FC = () => (
  <section style={{ marginTop: 96 }}>
    <SectionLabel left="Regulatory posture" right="what we are and are not" />
    <div style={{
      marginTop: 32, padding: '36px 40px',
      background: palette.paper2, border: `1px solid ${palette.line}`,
      borderRadius: 4, position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 40, width: 40, height: 3,
        background: palette.gold,
      }} />

      <NotBlock />
      <IsBlock />

      <p style={{
        fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.65,
        color: palette.ink3, margin: '32px 0 0',
        paddingTop: 20, borderTop: `1px solid ${palette.line}`,
      }}>
        WFS is not a registered investment adviser. Nothing on this site is
        personalized investment advice. Consult a registered investment
        adviser before making investment decisions.
      </p>
    </div>
  </section>
);

const NotBlock: React.FC = () => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.negative, marginBottom: 14,
    }}>Not</div>
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {[
        'A tip service',
        'A copy-trading platform',
        'A guru persona',
        'Personalized investment advice',
      ].map((s) => (
        <li key={s} style={{
          fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.6,
          color: palette.ink2, padding: '4px 0 4px 24px', position: 'relative',
        }}>
          <span style={{
            position: 'absolute', left: 0, top: 12, width: 12, height: 1,
            background: palette.negative,
          }} />
          {s}
        </li>
      ))}
    </ul>
  </div>
);

const IsBlock: React.FC = () => (
  <div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.positive, marginBottom: 14,
    }}>Is</div>
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {[
        'Notches — a research and trading platform organized around Understand, Evaluate, and Execute',
        'Public market research — recent briefings (today plus the last two days), the research library, and the public scorecard — free. The briefings archive and cross-asset analysis are Premier.',
        'Paid research plans — Researched Opportunities on stocks and equity options, index and ETF options, and futures options',
        'Automated trading engines — separate subscriptions for supported systematic strategies',
        'The Boring Bucket — a distinct mentored membership with study material and a member community, offered alongside the platform',
      ].map((s) => (
        <li key={s} style={{
          fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.6,
          color: palette.ink2, padding: '4px 0 4px 24px', position: 'relative',
        }}>
          <span style={{
            position: 'absolute', left: 0, top: 12, width: 12, height: 1,
            background: palette.positive,
          }} />
          {s}
        </li>
      ))}
    </ul>
  </div>
);

const ManagedSection: React.FC = () => (
  <section style={{ marginTop: 96 }}>
    <SectionLabel left="Professional engagements" right="distinct from self-service" />
    <div style={{
      marginTop: 32, padding: '36px 40px',
      border: `1px solid ${palette.line}`, background: palette.paper,
      borderRadius: 4, position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 40, width: 40, height: 3,
        background: palette.gold,
      }} />
      <SectionHead>
        A separate professional engagement track for RIAs and larger books.
      </SectionHead>
      <Body>
        Self-service retail memberships and automated engine subscriptions —
        Premier, the research plans, The Boring Bucket, and the Alpaca and
        Tastytrade auto-traders — are available directly from this site to
        any qualified individual.
      </Body>
      <Body>
        Professional engagements are a separate track. RIAs, family offices,
        and firms interested in a custom deployment or an ongoing service
        relationship contact the desk directly rather than subscribing here.
        Scope and eligibility are set in that conversation, not inferred from
        account size.{' '}
        <span style={{ color: palette.ink3, fontStyle: 'italic' }}>
          [Owner review — describe the actual current professional-service
          scope before publishing further detail here.]
        </span>
      </Body>
      <div style={{
        marginTop: 24, padding: '16px 20px',
        background: palette.paper2, border: `1px solid ${palette.line}`,
        borderRadius: 3, fontFamily: 'var(--sans)', fontSize: 13,
        color: palette.ink2, lineHeight: 1.6,
      }}>
        <strong style={{ color: palette.ink, fontWeight: 600 }}>Contact:</strong>{' '}
        <a href="mailto:bobby@bv-holdings.com" style={{
          color: palette.goldDeep, textDecoration: 'none',
          borderBottom: `1px solid ${palette.gold}60`,
        }}>bobby@bv-holdings.com</a>
      </div>
    </div>
  </section>
);

export default N2AboutPage;
